import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

// Sage Light buttons: flat, 8px radius, no shadow, no lift. One primary per screen.
//   default     -> Primary (accent fill, white text)
//   secondary   -> Secondary (inset fill, ink text)         (alias: outline)
//   ghost       -> Quiet (transparent, ink-55 -> ink)
//   destructive -> Destructive (breach-wash fill, breach text — never solid)
//   breach      -> the one solid red: a primary action on a breach surface
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-[15px] font-semibold leading-tight transition-[background-color,color,opacity] duration-state focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:stroke-[1.5]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:opacity-90",
        secondary: "bg-inset text-ink hover:bg-rule",
        outline: "bg-inset text-ink hover:bg-rule",
        ghost: "bg-transparent text-ink-55 hover:text-ink hover:bg-inset/60",
        destructive: "bg-breach-wash text-breach hover:bg-[#F6D2CA]",
        breach: "bg-breach text-white hover:opacity-90",
        link: "text-primary underline-offset-4 hover:underline h-auto p-0 font-medium",
      },
      size: {
        default: "min-h-[44px] px-[22px] py-[11px]",
        sm: "min-h-[36px] px-4 py-2 text-sm",
        lg: "min-h-[48px] px-6 py-3 text-base",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
